<template>
  <div
    :class="{
      'form-input': true,
      'form-input--required': data.required,
      'form-input--explicit-asterisk': data.required
    }"
    :data-validation="validationName"
  >
    <label class="form-input__label">
      <span class="form-input__caption">
        {{ data.caption }}
        <span v-if="showAsterisk" class="form-input__asterisk">*</span>
      </span>
      <FormVisibilityIcon
          v-if="data.visibility"
          class="form-input__visibility"
          v-tooltip="'Field is visible in formlist page'"
          color="#00A800"
        />
    </label>
    <TagAndConfigListInput
      ref="tagSelector"
      :type="type"
      :tags="currentLineTags"
      :tagGroupData="tagGroupData"
      :lastParentId="lastParentId"
      :timeLineEdit="viewOnly"
      :allowMultipleRoots="true"
      :disableSelectingAnotherParentTag="false"
      @addNewTag="onAddNewTag"
      @parentLevelSelected="onParentLevelSelected"
      @focusTagLevel="onFocusTagLevel"
      @resetSelectedLine="onResetSelectedLine"
    />
  </div>
</template>

<script>
import FormVisibilityIcon from '@/components/common/icons/formVisibilityIcon.vue'
import TagAndConfigListInput from '@/components/common/tagAndConfigListInput.vue'
import { TagTrie } from '@/utils/tagsHelper'

export default {
  components: { FormVisibilityIcon, TagAndConfigListInput },
  name: 'tags-component',
  props: {
    type: {
      type: Number,
      default: 3
    },
    mode: {
      type: String,
      default: 'TEMPLATE'
    },
    data: {
      type: Object,
      default: () => {}
    },
    value: {
      type: Array,
      default: () => []
    },
    viewOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      insert: [],
      delete: [],
      componentValue: [],
      tagTrie: new TagTrie(),
      attachedTags: [],
      tagGroupData: {
        firstLevelParents: [],
        tagArray: []
      },
      lastParentId: null,
      selected_tag_line: 0
    }
  },
  computed: {
    validationName () {
      return `tagsComponent-${this.data.field_id}`
    },
    showAsterisk () {
      return this.data.required && !String(this.data.caption).includes('*')
    },
    currentLineTags () {
      return this.tagGroupData?.tagArray?.[this.selected_tag_line] || []
    }
  },
  watch: {
    value (val) {
      this.initFromValue(val)
    }
  },
  created () {
    this.initFromValue(this.value)
  },
  methods: {
    initFromValue (val) {
      if ((this.mode === 'EDIT' || this.mode === 'VIEW' || this.mode === 'CREATE') && val?.length) {
        this.attachedTags = val.map(item => ({
          id: item.tag.id,
          name: item.tag.name,
          parentId: item.tag.parent_id
        }))
        this.tagTrie = new TagTrie()
        this.tagTrie._generateTreeFromUnorderedList(this.attachedTags)
        this.tagGroupData = this.tagTrie.groupTagInArray()
        const tagArray = this.tagTrie.getTagArray()
        this.componentValue = tagArray.map(tag => tag.id)
      }
    },
    normalizePayload (payload) {
      if (payload && typeof payload === 'object') return payload
      return { id: payload, rowIndex: this.selected_tag_line }
    },
    emitChange () {
      const value = {}
      if (this.insert.length) {
        value.insert = this.insert
      }
      if (this.delete.length) {
        value.delete = this.delete
      }
      this.$emit('customChange', {
        field_id: this.$props.data.field_id,
        value
      })
    },
    async validateSelection () {
      if (!this.$refs.tagSelector?.validateSelection) return true
      return this.$refs.tagSelector.validateSelection()
    },
    onAddNewTag (payload) {
      const { tag, rowIndex } = this.normalizePayload(payload)
      if (typeof rowIndex === 'number') this.selected_tag_line = rowIndex
      if (!tag) return

      this.tagTrie.insertTag({
        name: tag.name,
        id: tag.id,
        parentId: tag.parentId ?? tag.parent_id ?? null,
        abbreviation: tag.abbreviation ?? null
      })
      this.tagGroupData = this.tagTrie.groupTagInArray()

      if ((tag.parentId ?? tag.parent_id ?? null) === null) {
        this.onParentLevelSelected({ id: tag.id, rowIndex })
      }
      this.lastParentId = this.tagGroupData?.tagArray[this.selected_tag_line]?.at(-1)?.id ?? null

      const tagArray = this.tagTrie.getTagArray()
      this.componentValue = tagArray.map(t => t.id)

      if (this.mode === 'EDIT') {
        const isAlreadyAttached = this.attachedTags.find(item => item.id === tag.id)
        if (!isAlreadyAttached) {
          this.insert.push(tag.id)
          this.emitChange()
        } else {
          const idx = this.delete.indexOf(tag.id)
          if (idx !== -1) this.delete.splice(idx, 1)
        }
      }
    },
    onParentLevelSelected (payload) {
      const { id, rowIndex } = this.normalizePayload(payload)
      if (typeof rowIndex === 'number') this.selected_tag_line = rowIndex
      if (id === null) {
        if (typeof rowIndex !== 'number') this.selected_tag_line = 0
      } else {
        this.tagGroupData.tagArray.forEach((element, index) => {
          if (element[0]?.id === id) this.selected_tag_line = index
        })
      }
      this.lastParentId = this.tagGroupData?.tagArray[this.selected_tag_line]?.at(-1)?.id ?? null
    },
    onFocusTagLevel (payload) {
      const { index, rowIndex } = this.normalizePayload(payload)
      if (typeof rowIndex === 'number') this.selected_tag_line = rowIndex
      const currentLine = this.tagGroupData?.tagArray[this.selected_tag_line] || []
      const tagsToRemove = currentLine.slice(index)
      for (let i = tagsToRemove.length - 1; i >= 0; i--) {
        this.removeTagFromTrie(tagsToRemove[i].id)
      }
      this.tagGroupData = this.tagTrie.groupTagInArray()
      this.lastParentId = this.tagGroupData?.tagArray[this.selected_tag_line]?.at(-1)?.id ?? null
      const tagArray = this.tagTrie.getTagArray()
      this.componentValue = tagArray.map(t => t.id)
    },
    onResetSelectedLine (payload) {
      const { rowIndex } = this.normalizePayload(payload)
      if (typeof rowIndex === 'number') this.selected_tag_line = rowIndex
      const currentLine = this.tagGroupData?.tagArray[this.selected_tag_line] || []
      if (currentLine.length) {
        this.removeTagFromTrie(currentLine[0].id)
      }
      this.tagGroupData = this.tagTrie.groupTagInArray()
      this.selected_tag_line = 0
      this.lastParentId = null
      const tagArray = this.tagTrie.getTagArray()
      this.componentValue = tagArray.map(t => t.id)
    },
    removeTagFromTrie (tagId) {
      this.tagTrie.deleteTagById2(tagId)
      if (this.mode === 'EDIT') {
        this.removeChildTagsFromEdit(tagId)
      }
    },
    removeChildTagsFromEdit (tagId) {
      let isAlreadyAttached = false
      for (const tag of this.attachedTags) {
        if (tag.id === tagId) isAlreadyAttached = true
        if (tag.parentId === tagId) this.removeChildTagsFromEdit(tag.id)
      }
      if (isAlreadyAttached) {
        this.delete.push(tagId)
        this.emitChange()
      } else {
        const idx = this.insert.indexOf(tagId)
        if (idx !== -1) this.insert.splice(idx, 1)
      }
    }
  },
  beforeDestroy () {
    this.$emit('update-tags', [])
  }
}
</script>

<style>
</style>
